import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';

import type { PredictionSide, PricePrediction, PricePredictionMe } from '@flipster/apis/v1/price-prediction';
import { getPricePrediction, getPricePredictionMe, postPricePrediction } from '@flipster/apis/v1/price-prediction';
import { pswapTickersBunja } from '@flipster/r230522/original/pswap-ticker';
import { getTickerDisplayPrice } from '@flipster/r230522/original/ticker-price';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import Num from '@flipster/utils/num';

import { authBunja } from '../../../../shared/auth/state';
import { CompetitionIdScope } from '../../state';
import type { MilestoneDataSource, MilestoneStatus } from '../milestone/state';
import { ONE_DAY_MS, nanoToMs } from './utils';

const targetTicker = 'BTCUSDT.PERP';
// NOTE: BTC-PERP 가격 정밀도는 1자리 Snapshot을 호출하지 않고 가져오기 위함.
export const btcPricePrecision = 1;

const getMsUntilUtcMidnight = () => {
  const now = new Date();
  const nextMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0));
  return nextMidnight.getTime() - now.getTime();
};

export const pricePredictionBunja = bunja(() => {
  const id = bunja.use(CompetitionIdScope);
  const store = bunja.use(JotaiStoreScope);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const { pswapTickersAtom, setCurrentTicker } = bunja.use(pswapTickersBunja);

  // Subscribe to BTC-PERP ticker
  bunja.effect(() => {
    setCurrentTicker(targetTicker);
    return () => setCurrentTicker(null);
  });

  const btcCurrentPriceAtom = atom((get) => {
    const tickers = get(pswapTickersAtom);

    if (!tickers) return null;
    const btcTicker = tickers[targetTicker];
    return getTickerDisplayPrice(btcTicker) ?? null;
  });

  const pricePredictionQueryAtom = atomWithQuery<{ data: PricePrediction }>((get) => ({
    queryKey: ['getPricePrediction', id, get(isCookieLoggedInAtom)],
    queryFn: () => getPricePrediction(id),
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5,
  }));

  const pricePredictionAtom = atom((get) => get(pricePredictionQueryAtom).data?.data);
  const roundInfoAtom = atom((get) => {
    const prediction = get(pricePredictionAtom);
    const eventStart = nanoToMs(prediction?.eventStartTime);
    const eventEnd = nanoToMs(prediction?.eventEndTime);
    const now = Date.now();

    const totalRounds = eventStart && eventEnd ? Math.ceil((eventEnd - eventStart) / ONE_DAY_MS) : 1;
    const currentRound = eventStart ? Math.floor((now - eventStart) / ONE_DAY_MS) + 1 : 1;

    return {
      round: Math.max(1, Math.min(currentRound, totalRounds)),
      totalRounds,
    };
  });

  const pricePredictionMeQueryAtom = atomWithQuery<{ data: PricePredictionMe }>((get) => ({
    queryKey: ['getPricePredictionMe', id, get(isCookieLoggedInAtom)],
    queryFn: () => getPricePredictionMe(id),
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5,
    enabled: get(isCookieLoggedInAtom),
  }));

  const pricePredictionMeAtom = atom((get) => get(pricePredictionMeQueryAtom).data?.data);

  const refetchQueries = () => {
    store.get(pricePredictionQueryAtom).refetch();
    store.get(pricePredictionMeQueryAtom).refetch();
  };

  const postPredictionMutationAtom = atomWithMutation(() => ({
    mutationFn: (predictionSide: PredictionSide) => postPricePrediction(id, { predictionSide }),
    onSuccess: refetchQueries,
  }));

  bunja.effect(() => {
    const scheduleRefetch = () => {
      const msUntilMidnight = getMsUntilUtcMidnight();

      return setTimeout(() => {
        refetchQueries();

        scheduleRefetch();
      }, msUntilMidnight);
    };

    const timeoutId = scheduleRefetch();
    return () => clearTimeout(timeoutId);
  });

  return {
    pricePredictionAtom,
    pricePredictionMeAtom,
    postPredictionMutationAtom,
    btcCurrentPriceAtom,
    roundInfoAtom,
  };
});
pricePredictionBunja.debugLabel = 'pricePredictionBunja';

export const pricePredictionMilestoneDataSourceBunja = bunja(() => {
  const { pricePredictionAtom } = bunja.use(pricePredictionBunja);

  const statusAtom = atom((get) => {
    const pp = get(pricePredictionAtom);
    return (pp?.pricePredictionStatus ?? 'UPCOMING') as MilestoneStatus;
  });

  const currentMilestoneValueAtom = atom((get) => {
    const pp = get(pricePredictionAtom);
    return Num(pp?.currentMilestoneValue ?? 0);
  });

  const maxPrizePoolAtom = atom((get) => {
    const pp = get(pricePredictionAtom);
    const milestones = pp?.milestones?.VOLUME ?? [];
    if (milestones.length === 0) return Num(0);
    return Num(milestones[milestones.length - 1]?.prize ?? 0);
  });

  const milestonesAtom = atom((get) => {
    const pp = get(pricePredictionAtom);
    const milestones = pp?.milestones?.VOLUME ?? [];
    return milestones.map(({ prize, value }) => ({
      prize: Num(prize),
      value: Num(value),
    }));
  });

  const dataSource: MilestoneDataSource = {
    statusAtom,
    currentMilestoneValueAtom,
    maxPrizePoolAtom,
    milestonesAtom,
  };

  return {
    dataSource,
  };
});
pricePredictionMilestoneDataSourceBunja.debugLabel = 'pricePredictionMilestoneDataSourceBunja';
